import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@app/core/api/auth/auth.service';
import { InvestService, InvestNarrativeResponse } from '@app/core/api/users/invest.service';
import { formatDateLong } from '@app/helpers/utils/date.utils';
import { ModalService } from '@app/shared/components/modal/modal.service';
import { ToastService } from '@app/shared/components/toast/toast.service';
import { InputComponent } from '@app/shared/forms/input/input.component';
import { QuillEditorComponent } from '@app/shared/forms/quill-editor/quill-editor.component';
import { LoaderCircle, LucideAngularModule } from 'lucide-angular';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-narrative',
  standalone: true,
  imports: [InputComponent, QuillEditorComponent, CommonModule, FormsModule, ReactiveFormsModule, LucideAngularModule],
  templateUrl: './narrative.component.html',
  styleUrl: './narrative.component.scss'
})
export class NarrativeComponent implements OnInit, OnDestroy {
  authService = inject(AuthService);
  investService = inject(InvestService);
  modalService = inject(ModalService);
  toastService = inject(ToastService);
  private router = inject(Router);

  @ViewChild('updateProfileModal') updateProfileModal!: TemplateRef<any>;

  type: 'Investor' | 'Investee' = 'Investor';
  Math = Math;
  id: number = 0;
  code: string = '';
  content: string = '';
  lastUpdated: string = '';

  destroy$ = new Subject<void>();

  form = new FormGroup({
    content: new FormControl(''),
  });

  readonly Loader = LoaderCircle;

  loading: boolean = false;
  isOwner: boolean = false;
  allUsers: any;

  readonly currentUser = this.authService.user();
  userProfile: any = [];

  profileUpdateRequired: boolean = false;
  lastProfileUpdated: string | null = null;
  nextUpdateDue: string | null = null;

  ngOnInit(): void {
    const urlData = this.investService.getState();
    this.id = urlData?.id ?? null;
    this.code = urlData?.code ?? null;
    this.type = urlData?.type ?? 'Investor';

    this.loadProfile();
    this.form.disable();

    this.form.patchValue({
      content: this.userProfile?.content ?? 'NO PUBLIC PROFILE'
    });

    const ownEntityId = this.currentUser?.invest_id;
    if (ownEntityId != null && String(ownEntityId) === String(this.id)) {
      this.investService.checkProfileUpdateRequired(this.id.toString()).subscribe((res) => {
        this.profileUpdateRequired = res.required;
        this.lastProfileUpdated = res.lastUpdated;
        this.nextUpdateDue = res.nextReminderDate;
      });
    }
  }

  modal() {
    const modal = this.modalService.open({
      title: 'Update Public Profile',
      contentTemplate: this.updateProfileModal,
      size: 'lg',
      contentData: {
        onConfirmRequest: () => {
          this.router.navigate(['application-registration/1/update']);
          this.modalService.close(modal);
        },
        onClose: () => {
          this.modalService.close(modal);
        },
      },
      position: 'center',
      animation: 'fade',
    });
  }

  loadProfile() {
    this.loading = true;

    this.investService
      .getInvestProfile(this.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: InvestNarrativeResponse) => {
          this.userProfile = res.data;
          this.isOwner = this.currentUser?.invest_id == res.data.invest_entity_id;

          this.lastUpdated = formatDateLong(this.userProfile?.updated_at) ?? '';
          this.form.get('content')?.setValue(this.userProfile?.content ?? 'NO PUBLIC PROFILE');
          if (!this.isOwner) {
            this.form.get('content')?.disable();
          }

          if (this.profileUpdateRequired) {
            this.modal();
          }
          
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  updateNarrative() {
    if (!this.isOwner) return; // Prevent non-owners from updating

    this.loading = true;

    if (this.form.valid) {
      if (this.profileUpdateRequired) {
        this.modal();
        return;
      }
      const contentValue: string = this.form.get('content')?.value ?? '';
      console.log('Quill content:', contentValue);

      this.investService
        .updateProfileNarrative(this.id, { content: contentValue })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.toastService.show({
              title: 'Success',
              content: `Public profile updated successfully`,
              type: 'success',
              duration: 5000,
            });
            this.loadProfile();
            this.loading = false;
          },
          error: (error) => {
            let message = 'Something went wrong';
            if (error?.error?.errors?.content) {
              message = error.error.errors.content.join(', ');
            } else if (error?.error?.message) {
              message = error.error.message;
            }
            this.toastService.show({
              title: 'Error',
              content: message,
              duration: 3000,
              position: 'right',
              type: 'error',
            });
            this.loading = false;
          },
        });
    }
  }

  onTextChange(event: any) {
    console.log('HTML:', event.html);
    console.log('Text:', event.text);
    console.log('Delta:', event.delta);
  }

  /**
   * Cleans up subscriptions to prevent memory leaks.
   * @returns void
   */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.investService.clearState();
  }
}